import { ref, toRaw, unref, watch, watchEffect } from 'vue';

import type { Ref, UnwrapRef } from 'vue';

/**
 * Хук для работы с состоянием как в react.js на основе ref vue 3
 * Может быть удобен в некоторых случаях
 * @param defaultStateValue
 */
export function useState<T, R = Ref<T>>(defaultStateValue?: T | (() => T)): [R, (val: T) => void] {
  const initValue: T = typeof defaultStateValue === 'function' ? (defaultStateValue as any)() : defaultStateValue;

  const innerValue = ref(initValue) as Ref<T>;

  function triggerChange(newValue: T) {
    innerValue.value = newValue;
  }

  return [innerValue as unknown as R, triggerChange];
}

export function useMergedState<T, R = Ref<T>>(
  defaultStateValue: T | (() => T),
  option?: {
    defaultValue?: T | (() => T);
    value?: Ref<T> | Ref<UnwrapRef<T>>;
    onChange?: (val: T, prevValue: T) => void;
    postState?: (val: T) => T;
  }
): [R, (val: T) => void] {
  const { defaultValue, value = ref() } = option || {};

  let initValue: T = typeof defaultStateValue === 'function' ? (defaultStateValue as any)() : defaultStateValue;

  if (value.value !== undefined) {
    initValue = unref(value as any) as T;
  }

  if (defaultValue !== undefined) {
    initValue = typeof defaultValue === 'function' ? (defaultValue as any)() : defaultValue;
  }

  const innerValue = ref(initValue) as Ref<T>;
  const mergedValue = ref(initValue) as Ref<T>;

  watchEffect(() => {
    let val = value.value !== undefined ? value.value : innerValue.value;

    if (option?.postState) {
      val = option.postState(val as T);
    }

    mergedValue.value = val as T;
  });

  function triggerChange(newValue: T) {
    const prevVal = mergedValue.value;
    innerValue.value = newValue;

    if (toRaw(mergedValue.value) !== newValue && option?.onChange) {
      option.onChange(newValue, prevVal);
    }
  }

  // Effect of reset value to `undefined`
  watch(value, () => {
    innerValue.value = value.value as T;
  });

  return [mergedValue as unknown as R, triggerChange];
}
